import { Injectable } from '@angular/core';
import { MotesGeolocation } from './motes-geolocation'

import { Coords, GeolocationOptions, GeolocationPosition, isPositionError } from '../models';
import { BehaviorSubject, Subscription, Observable, ReplaySubject, animationFrameScheduler, MonoTypeOperatorFunction } from 'rxjs';
import { first, map, shareReplay, tap, throttleTime } from 'rxjs/operators';


export enum GpsState {
    WAITING_FOR_INIT = 'WAITING_FOR_INIT',   // GPS service has not ben initialized
    SERVICE_INITIALIZED = 'SERVICE_INITIALIZED',// GPS service has ben initialized but no position valid / pending
    ACCESS_ALLOWED = 'ACCESS_ALLOWED',     // User allowed access
    ACCESS_DENIED = 'ACCESS_DENIED',      // User disallowed access
    FIRST_VALID = 'FIRST_VALID',        // First valid position recieved
    POS_INVALID = 'POS_INVALID',        // Invalid positions recieved over a longer period of time
    BAD_ACCURACY = 'BAD_ACCURACY',       // Invalid positions - too bad accuracy
    POS_VALID = 'POS_VALID'           // Position has become valid again after period of invalid/inaccurate updates
}

function positionTimeout<T>(promise: Promise<T>, timeoutMs: number) : Promise<T>{
    return Promise.race([
        promise,
        // Obs this will fire no matter if we timeout or not but it does not really matter (just don't print any error here)
        new Promise<null>( resolve => setTimeout( () => {
            // console.warn("User did not give GPS permission before timeout");
            resolve(null);
        }, timeoutMs))
    ]);
}

@Injectable({ providedIn: 'root' })
export class PositionService {
    private static MAX_ACCURACY = 2000;
    // Implement 1000 sec GPS update hysteresis
    private static THROTTLE_TIME = 1000;
    private static FIRST_POSITION_TIMEOUT = 15000;

    private initialized: boolean = false;
    private positionDebug: boolean = false;
    private stateDebug: boolean = false;

    public firstValidPosRecieved: boolean = false;
    private stateSubject = new BehaviorSubject<GpsState>(GpsState.WAITING_FOR_INIT);

    private stateNotificationSubject = new ReplaySubject<GpsState>(0);

    // Internal subscriptions
    private watchSubscription: Subscription;
    private restartInterval: any;

    private positionSubject: BehaviorSubject<Coords>;

    private emulatedPosition: Coords = null; //new Coords(54.98676492091076, 12.44060374500188);
    private emulateAccuracy: boolean = false;

    // Options for geolocation
    private readonly options: GeolocationOptions = { maximumAge: 950, enableHighAccuracy: true };

    public lastKnownPosition: Coords = new Coords(0, 0, false);
    public lastKnownAccuracy: number = 0;

    constructor(private geolocation: MotesGeolocation) {
        if(!geolocation.watchPosition) {
            console.error("No service for Motes geolocation selected \n \
            Please define provider in app.module like: \n \
            { provide: MotesGeolocation, useClass: Geolocation }");
        }
        // Create position observer on first call
        this.positionSubject = new BehaviorSubject<Coords>(this.lastKnownPosition);
    }

    public init(emulateAccuracy: boolean = false): BehaviorSubject<GpsState> {
        if(this.stateDebug) console.log("<GpsState> Init GPS service called...");

        // Initialize service if not already initialized
        if(!this.initialized) {
            this.emulateAccuracy = emulateAccuracy;

            // Start watching position (ask for user for permission to get location now)
            this.initialized = true;
            this.setState(GpsState.SERVICE_INITIALIZED);
        }

        // Init positionSubject
        if(!this.firstValidPosRecieved && this.geolocation.watchPosition) {
            this.startWatchingPosition();
        }
        return this.stateSubject;
    }

    public watchGpsState(): BehaviorSubject<GpsState> {
        return this.stateSubject;
    }

    public watchPosition(): Observable<Coords> {

        // OBS. Dont start watching position until service have been initialized
        // Return promise which will get called when position initialized
        return this.positionSubject.pipe(shareReplay(1));
    }

    public getPosition(): Promise<Coords> {
        return this.watchPosition().pipe(first()).toPromise();
    }

    /**
     * Enable/disable console debug for GPS state and position messages
     */
    public enableDebug(debugLevel: 'state' | 'verbose') {
        this.stateDebug = (debugLevel == 'state' || debugLevel == 'verbose');
        this.positionDebug = (debugLevel == 'verbose');
    }

    /**
     * Return true if the coords are valid
     */
    private validateCoords(position: GeolocationPosition | GeolocationPositionError): position is GeolocationPosition {
        if(this.hasPositionError(position)) return false;
        // A nice position was received
        if(!this.firstValidPosRecieved) this.setState(GpsState.ACCESS_ALLOWED);
        return true;
    }

    private hasPositionError(position: GeolocationPosition | GeolocationPositionError): position is GeolocationPositionError {
        if(!isPositionError(position)) return false;

        // If user denied giving position
        if(position.code == position.PERMISSION_DENIED) {
            this.setState(GpsState.ACCESS_DENIED);
            return true;
        }

        if(position.code == position.POSITION_UNAVAILABLE) {
            console.warn("User denied GPS access or protocol does not allow it");
            this.setState(GpsState.ACCESS_DENIED);
            return true;
        }

        if(position.code == position.TIMEOUT) {
            console.warn("Got position timeout from GPS");
            this.setState(GpsState.POS_INVALID);
            return true;
        }
        // Reset last known accuracy
        this.lastKnownAccuracy = 0;

        return true;
    }

    /**
     * Start or restart watching position.
     * Get one GPS position initially and then start or restart watching for position
     */
    public async startWatchingPosition() {
        if(this.stateDebug) console.log("<GpsState> Get current (first) GPS position once...");

        // Get the first position
        let position = await positionTimeout(this.geolocation.getCurrentPosition(this.options), PositionService.FIRST_POSITION_TIMEOUT).catch((err: GeolocationPositionError) => {
            // This object is fucked but this will be handled by validateCoords()
            console.log("Could no start watching position (getCurrentPosition throw error)", err);
            this.validateCoords(err);
            return null;
        });

        // If we could not getCurrentPosition, give up watching position
        if(!position) {
            // For now, assume that access was denied in case there is no / null position
            // Current version of the native app sends a null on permission denied
            // (delete this when new versions of Android and iOS > v1.0.22 are released)
            this.setState(GpsState.ACCESS_DENIED);
            return;
        }

        let accessValid = this.validateCoords(position);

        // Only notify listners if GPS access was given
        if(accessValid) this.notifyListeners(position);

        // Cancel any old subscriptions
        if(this.watchSubscription) this.watchSubscription.unsubscribe();

        // Start watching
        let lastCoords: GeolocationPosition = null;
        const selectBest = map((position: GeolocationPosition) => {
            if(!lastCoords) {
                lastCoords = position;
                return position;
            }

            let bestPosition = (position.coords.accuracy < lastCoords.coords.accuracy) ? position : lastCoords;
            return bestPosition;
        });

        const resetBest = tap<GeolocationPosition>(() => { lastCoords = null });

        if(this.stateDebug) console.log("<GpsState> Start watching GPS position...");
        // Subscribe to position updates
        this.watchSubscription = this.geolocation.watchPosition(this.options).pipe(selectBest, this.throttlePipe(), resetBest).subscribe((position) => {
            this.notifyListeners(position);
        });
    }

    private throttlePipe<T>(): MonoTypeOperatorFunction<T> {
        return throttleTime<T>(PositionService.THROTTLE_TIME, animationFrameScheduler, { leading: true, trailing: true });
    }

    private setState(state: GpsState) {
        if(this.stateDebug) console.log("<GpsState> GPS state change: " + state);
        this.stateSubject.next(state);
    }

    private notifyListeners(position: GeolocationPosition | GeolocationPositionError) {
        if(!this.validateCoords(position)) return;

        if(this.emulatedPosition) {
            if(this.positionDebug) console.log(`<GpsVerbose> Reject real position (${position.coords.latitude, position.coords.longitude}), accuracy: ${this.lastKnownAccuracy}`);
            // Dont do anything, position is emulated
            return;
        }

        // Save accuracy for display
        this.lastKnownAccuracy = (position && position.coords) ? Math.round(position.coords.accuracy * 10) / 10 : -1;
        if(this.positionDebug) console.log(`<GpsVerbose> Got GPS position: (${position.coords.latitude, position.coords.longitude}), accuracy: ${this.lastKnownAccuracy}`);

        // Check if some error occurred (this should never really happen as we already validated the coords)
        if(!position || !position.coords || (position.coords.latitude == 0 && position.coords.longitude == 0)) {
            console.log("There seems to be no coords on position object: ", position);
            this.lastKnownPosition.setPosition(0, 0, false);
            this.setState(GpsState.POS_INVALID);
            return;
        }

        // Reject GPS positions with (in)accuracy bigger than 600m
        if(!this.emulateAccuracy && position.coords.accuracy > PositionService.MAX_ACCURACY) {
            console.log("Rejected pos: (" + position.coords.latitude + ", " + position.coords.longitude + ") acc: " + Math.floor(position.coords.accuracy));
            this.setState(GpsState.BAD_ACCURACY);
            return;
        }

        // Get current state of GPS statemachine
        let currentState = this.stateSubject.getValue();

        // Notify listners if this is the first valid GPS pos
        if(!this.firstValidPosRecieved) {
            this.firstValidPosRecieved = true;
            this.setState(GpsState.FIRST_VALID);
        }
        // Notify listners if state changed
        else if(currentState != GpsState.POS_VALID) {
            this.setState(GpsState.POS_VALID);
        }

        // YES We got a nice position - save as current position
        this.lastKnownPosition.setPosition(position.coords.latitude, position.coords.longitude, true);
        this.lastKnownPosition.setHeading(position.coords.heading, position.coords.speed);

        // Notify listeners about new position
        //console.log("Got valid pos: (" + position.coords.latitude + ", " + position.coords.longitude + ") acc: " +  Math.floor(position.coords.accuracy));
        this.positionSubject.next(this.lastKnownPosition);
    }

    public emulatePosition(coords: Coords): void {
        if(coords) {
            // Always set emulated coords as valid
            coords.isValid = true;
            this.emulatedPosition = coords;

            // If we dont already emulate position, send a first
            if(!this.emulatedPosition) {
                // Notify about first emulated position
                this.lastKnownPosition = this.emulatedPosition;
                this.setState(GpsState.FIRST_VALID);
                this.positionSubject.next(this.emulatedPosition);
            }

            // Notify about emulated position after some time after first
            setTimeout(() => {
                this.lastKnownPosition = this.emulatedPosition;
                this.setState(GpsState.POS_VALID);
                this.positionSubject.next(this.emulatedPosition);
            }, 300)
        }
    }

    public cancelEmulatedPosition() {
        this.emulatedPosition = null;
    }

    public currentState(): GpsState {
        return this.stateSubject.getValue();
    }

    public currentStateString() {
        return GpsState[this.stateSubject.getValue()] || $localize`Undefined?`;
    }

    public currentStatusString() {
        switch(this.stateSubject.getValue()) {
            case GpsState.ACCESS_ALLOWED:
                return $localize`Location access allowed. Waiting for some satellites to wake up...`;
            case GpsState.ACCESS_DENIED:
                return $localize`Location access seems to be denied - by you?`;
            case GpsState.POS_INVALID:
                if(this.firstValidPosRecieved) {
                    return $localize`Your GPS and I had some great times together but we lost it. Is it still enabled? Accuracy\: ${this.lastKnownAccuracy}m.`;
                }
                else {
                    return $localize`Getting some weird location signals. Is you GPS power-napping, out of signal or disabled? Accuracy\: ${this.lastKnownAccuracy}m.`;
                }
            case GpsState.BAD_ACCURACY:
                return $localize`The location accuracy is very bad - I can't talk with any of my satellite friends from here`;
            case GpsState.FIRST_VALID:
            case GpsState.POS_VALID:
                let accuracyStr = (this.lastKnownAccuracy > 300) ? $localize`bad, but acceptable` : $localize`good`;
                let speed = this.lastKnownPosition && this.lastKnownPosition.properties.speed > 0 ? $localize`Speed\: ${this.lastKnownPosition.properties.speed}m/s.` : "";
                let heading = this.lastKnownPosition && this.lastKnownPosition.properties.heading >= 0 ? $localize`Direction\: ${this.lastKnownPosition.properties.heading}°.` : "";

                return $localize`Your GPS signal is healthy and enjoying life. Accuracy is ${accuracyStr}\: ${this.lastKnownAccuracy}m. ${speed}`;
            case GpsState.SERVICE_INITIALIZED:
                return $localize`Waiting for location... Something seems wrong, maybe your GPS is power-napping?`;
            case GpsState.WAITING_FOR_INIT:
                return $localize`Waiting for location... It does not seems to be enabled yet.`;
            default:
                return $localize`Something is wrong with the creators of this APP, we cannot find your location.`;
        }
    }

    /**
    * Emit current state of GPS to view notification components
    */
    public emitStateNotification() {
        this.stateNotificationSubject.next(this.currentState());
    }

    /**
    * Emit current state of GPS to view notification components
    */
    public emitAnyWarnings() {
        let state = this.currentState();
        if(state == GpsState.ACCESS_DENIED || state == GpsState.BAD_ACCURACY || state == GpsState.POS_INVALID) {
            this.emitStateNotification();
        }
    }

    /**
    * Watch any user invoked GPS state notifications
    */
    public watchStateNotifications(): Observable<GpsState> {
        return this.stateNotificationSubject;
    }

    /**
     * Get users GPS permission state (useful to check if permission is already granted before prompting user)
     */
    public async getGpsPermissions() : Promise<PermissionState> {
        return this.geolocation.getPermissions();
    }

    /**
    * Destroy all internal service subscriptions
    */
    public destroy() {
        if(this.watchSubscription) this.watchSubscription.unsubscribe();
        if(this.restartInterval) clearInterval(this.restartInterval);

        this.watchSubscription = null;
        this.restartInterval = null;
    }
}
